/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/*
 * Maven (hackathon): what the admin is looking at, shared by every AI Admin screen.
 * The page name comes from the URL for every screen (AIAdminContainer). A screen that wants Maven to see its
 * filters calls setPageFilters(page, payload) where it already runs its search. Only the fields below are
 * kept, so user ids, emails, paging and tenant values never reach the AI.
 */
import { action, observable } from 'mobx';

const ALLOWED_FILTERS = ['startDate', 'endDate', 'graphId', 'graphVersion', 'agentName', 'type', 'status', 'requestId'];

const state = observable({ page: null, filters: null });

const pickFilters = (payload = {}) => {
  const entries = ALLOWED_FILTERS.map((key) => [key, payload[key]]).filter(
    ([, value]) => value !== undefined && value !== null && value !== ''
  );
  return entries.length ? Object.fromEntries(entries) : null;
};

/** Saves the filters of the last search on a screen. */
export const setPageFilters = action((page, payload) => {
  state.page = page;
  state.filters = pickFilters(payload);
});

/** Clears the saved filters (e.g. on reset). */
export const clearPageFilters = action(() => {
  state.page = null;
  state.filters = null;
});

/**
 * The page context to send with a question: the page name, plus that page's filters if it saved any.
 * Filters saved on another screen are ignored.
 * @param {string} page - the current screen (tab name from the URL)
 * @returns {{ page: string, filters?: object } | null}
 */
export const getPageContext = (page) => {
  if (!page) return null;
  return state.page === page && state.filters ? { page, filters: { ...state.filters } } : { page };
};
